import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Card, Button, message, Row, Col } from 'antd';
import ProductInfoSection from './ProductInfoSection';
import ProductVariationsSection from './ProductVariationsSection';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  getProductById,
  updateProduct, 
  createProduct
} from '../../../core/api/products';

const ProductForm = ({ 
  productId, 
  clientId, 
  onSuccess, 
  onCancel 
}) => {
  const navigate = useNavigate();
  
  const [form] = Form.useForm();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const selectedClients = useSelector((state) => state.selectedClients);

  const uniqueId = useMemo(() => {
    return `${productId || 'new'}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, [productId]);
  
  const fetchProductData = useCallback(async () => {
    try {
      console.log('Fetching product data for productId:', productId);
      setLoading(true);
      const response = await getProductById(productId);
      console.log('Product data received:', response.data);
      
      if (response && response.data) {
        setProduct(response.data);
        // Initialize form with product data
        form.setFieldsValue({
          name: response.data.name,
          product_code: response.data.product_code,
          internal_code: response.data.internal_code,
          product_type: response.data.product_type,
          product_universe: response.data.product_universe,
          brand: response.data.brand,
        });
      }
    } catch (error) {
      message.error('Failed to fetch product data');
      console.error('Error fetching product data:', error);
    } finally {
      setLoading(false);
    }
  }, [productId, form]);
  
  useEffect(() => {
    if (productId) {
      fetchProductData();
    }
  }, [productId, fetchProductData]);
  



  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      
      const productData = {
        ...values,
        client_id: clientId || (selectedClients && selectedClients[0])
      };
      
      let response;
      
      if (productId) {
        response = await updateProduct(productId, productData);
        message.success('Product updated successfully');
        
        // Refresh product data after update
        fetchProductData();
        
        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        // Creating a new product
        response = await createProduct(productData);
        message.success('Product created successfully');
        
        // Instead of calling onSuccess which would close the form,
        // we'll redirect to the edit view of the newly created product
        const newProductId = response.data.id;
        
        // Update the URL to the edit product view and force a full page reload
        // This ensures the component is fully reloaded with the edit view
        // URL format should be: /products/edit/{clientId}/{productId}/{variationId}/{warehouseId}
        const effectiveClientId = clientId || (selectedClients && selectedClients[0]) || '';
        window.location.href = `/products/edit/${effectiveClientId}/${newProductId}`;
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save product');
      console.error('Error saving product:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          name: '',
          product_code: '',
          internal_code: '',
        }}
      >
        <Row gutter={16}>
          <Col xs={24}>
            <Card 
              title={productId ? "Edit Product" : "Add New Product"}
              bordered={false}
              className="mb-4"
            >
              <ProductInfoSection 
                form={form} 
                product={product} 
                uniqueId={uniqueId}
                clientId={clientId}
              />
            </Card>
            
            {productId && (
              <Card 
                title="Product Variations" 
                bordered={false}
              >
                <ProductVariationsSection 
                  productId={productId} 
                  uniqueId={uniqueId}
                  refreshProduct={fetchProductData}
                />
              </Card>
            )}
          </Col>
        </Row>
        
        <div className="d-flex justify-content-end mt-4">
          <Button 
            onClick={onCancel} 
            className="me-2"
          >
            Cancel
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={submitting}
          >
            {productId ? 'Update Product' : 'Create Product'}
          </Button>
        </div>
      </Form>
  );
};

export default ProductForm;
